import React, { useContext, useEffect, useState } from 'react';
import moment from 'moment-timezone';
import { useNavigate } from 'react-router-dom';
import { StateContext } from '../../StateProvider';
import { Container } from 'react-bootstrap';
import './BookingDashboard.css';
import { addCartItem } from '../../Utilities/api';

import Popup from '../../Utilities/components/Popup';
import formatDateRange from '../../Utilities/functions/FormatDateRange';
import cartIcon from '../../../assets/Dashboard/storeIcon.png';

import HotelBookingCalculator from '../../Utilities/components/Calculations/HotelBookingCalculator';
import DaycareBookingCalculator from '../../Utilities/components/Calculations/DaycareBookingCalculator';
import GroomingBookingCalculator from '../../Utilities/components/Calculations/GroomingBookingCalculator';
import TrainingBookingCalculator from '../../Utilities/components/Calculations/TrainingBookingCalculator';
import AdventureBookingCalculator from '../../Utilities/components/Calculations/AdventureBookingCalculator';
import ShuttleBookingCalculator from '../../Utilities/components/Calculations/ShuttleBookingCalculator';
import MembershipBookingCalculator from '../../Utilities/components/Calculations/MembershipBookingCalculator';
import BundleBookingCalculator from '../../Utilities/components/Calculations/BundleBookingCalculator';
import GiftCardPurchaseCalculator from '../../Utilities/components/Calculations/GiftCardPurchaseCalculator';

const BookingDashboard = () => {
  const navigate = useNavigate();
  const {
    bookingData,
    setBookingData,
    cart,
    setCart,
    user,
    serviceDetails,
    refreshUserData,
  } = useContext(StateContext);

  const cartItemCount = Array.isArray(cart) ? cart.length : 0;
  const [showPopup, setShowPopup] = useState(false);
  const [popupMessage, setPopupMessage] = useState('');
  const [serviceId, setServiceId] = useState(0);

  useEffect(() => {
    if (serviceDetails && bookingData && bookingData.bookingType) {
      const foundServiceId = findServiceId(serviceDetails, bookingData.bookingType, bookingData);
      setServiceId(foundServiceId || null);
    } else {
      setServiceId(null);
    }
  }, [serviceDetails, bookingData]);

  // RE-RENDER once user loads so discounts can apply
  useEffect(function () {
    if (user) {
      // just triggers a re-render so calculatePrice sees updated userCommunities
      setShowPopup(false);
    }
  }, [user]);

  // Location-based price modifier
  const locationModifiers = {
    Anaheim: 0.8,
    'Los Angeles': 1.0,
  };

  const locationModifier =
    user && locationModifiers[user.storeLocation]
      ? locationModifiers[user.storeLocation]
      : 1;

  // Membership discount check
  function getMembershipDiscountPercent(user) {
    if (!user) return 0;
    var membership = null;
    if (user.clubMembership) membership = user.clubMembership;
    else if (user.ClubMemberships && user.ClubMemberships.length > 0)
      membership = user.ClubMemberships[0];

    if (
      membership &&
      membership.billingStatus === 'active' &&
      !membership.cancelledAt
    ) {
      if (membership.planType === 'Plus') return 20;
      if (membership.planType === 'Club') return 10;
    }
    return 0;
  }

  function getCommunityDiscountPercent(user) {
    if (!user || !user.userCommunities) return 0;

    var highest = 0;

    for (var i = 0; i < user.userCommunities.length; i++) {
      var c = user.userCommunities[i];

      // community discount lives here
      var percent =
        c &&
        c.CommunityInfo &&
        c.CommunityInfo.discountPercent
          ? c.CommunityInfo.discountPercent
          : 0;

      if (percent > highest) highest = percent;
    }

    return highest;
  }

  // --- PRICE CALCULATION ---
  const calculatePrice = () => {
    var calculatedPrice;
    switch (bookingData.bookingType) {
      case 'store/membership':
        calculatedPrice = MembershipBookingCalculator(
          bookingData.bookingInfo || {},
          serviceDetails
        );
        break;
      case 'store/bundle':
        calculatedPrice = BundleBookingCalculator(bookingData.bookingInfo || {});
        break;
      case 'store/services/gift-card-purchase':
        calculatedPrice = GiftCardPurchaseCalculator(
          bookingData || {},
          serviceDetails
        );
        break;
      case 'store/services/daycare':
        calculatedPrice = DaycareBookingCalculator(
          bookingData.bookingInfo || {},
          serviceDetails,
          user
        );
        break;
      case 'store/services/hotel':
        calculatedPrice = HotelBookingCalculator(
          bookingData.bookingInfo || {},
          serviceDetails
        );
        break;
      case 'store/services/grooming':
        calculatedPrice = GroomingBookingCalculator(
          bookingData.bookingInfo || {},
          serviceDetails
        );
        break;
      case 'store/services/training':
        calculatedPrice = TrainingBookingCalculator(
          bookingData.bookingInfo || {},
          serviceDetails
        );
        break;
      case 'store/services/adventure':
        calculatedPrice = AdventureBookingCalculator(
          bookingData.bookingInfo || {},
          serviceDetails
        );
        break;
      case 'store/services/shuttle':
        calculatedPrice = ShuttleBookingCalculator({
          serviceDetails: serviceDetails,
          bookingInfo: bookingData.bookingInfo || {},
        });
        break;
      default:
        calculatedPrice = { originalPrice: 0, discountedPrice: 0 };
    }

    const base = {
      originalPrice: calculatedPrice.originalPrice * locationModifier,
      discountedPrice: calculatedPrice.discountedPrice * locationModifier,
    };

    // 🟩 Skip membership discount for membership or bundle purchases
    var type = bookingData && bookingData.bookingType ? bookingData.bookingType : '';
    var skipDiscount =
      type.indexOf('membership') !== -1 || type.indexOf('bundle') !== -1;

    if (!skipDiscount) {
      var memberDiscountPercent = getMembershipDiscountPercent(user);
      if (memberDiscountPercent > 0) {
        var discountFactor = 1 - memberDiscountPercent / 100;
        base.discountedPrice = base.discountedPrice * discountFactor;
      }
    }

    // COMMUNITY DISCOUNT
    var communityPercent = getCommunityDiscountPercent(user);
    if (communityPercent > 0) {
      var cFactor = 1 - communityPercent / 100;
      base.discountedPrice = base.discountedPrice * cFactor;
    }

    return base;
  };

  const calculated = React.useMemo(function () {
    return calculatePrice();
  }, [user, bookingData, serviceDetails, locationModifier]);

  const originalPrice = calculated.originalPrice;
  const discountedPrice = calculated.discountedPrice;

  // --- HELPERS ---
  function getHotelNights(dateRange) {
    if (!dateRange || dateRange.length !== 2) return 0;
    var start = moment(dateRange[0]).startOf('day');
    var end = moment(dateRange[1]).startOf('day');
    var nights = end.diff(start, 'days');
    return nights > 0 ? nights : 0;
  }

  function getDaycareDays(dateRange) {
    if (!dateRange || dateRange.length !== 2) return 0;
    var start = moment(dateRange[0]).startOf('day');
    var end = moment(dateRange[1]).endOf('day');
    var days = end.diff(start, 'days') + 1;
    return days > 0 ? days : 0;
  }

  // --- ADD TO CART ---
  const handleAddToCart = async () => {
    if (!serviceId) {
      setPopupMessage('Service ID not found, refresh the page or try again!');
      setShowPopup(true);
      return;
    }

    // Bundle Booking Error Handlers
    if (bookingData.bookingType === 'store/bundle') {
      if (!bookingData.bookingInfo || !bookingData.bookingInfo.selectedPackage) {
        setPopupMessage('Please select a bundle package.');
        setShowPopup(true);
        return;
      }
    }

    // --- UNIVERSAL SERVICE VALIDATION ---
    const info = bookingData.bookingInfo || {};
    const type = bookingData.bookingType;

    // ----- 1. Daycare -----
    if (type === 'store/services/daycare') {
      if (!info.selectedPets || info.selectedPets.length === 0) {
        setPopupMessage('Please select at least one pet for daycare.');
        setShowPopup(true);
        return;
      }
      if (!Array.isArray(info.dateRange) || info.dateRange.length !== 2) {
        setPopupMessage('Please select your daycare dates.');
        setShowPopup(true);
        return;
      }
        if (!info.foodPreference || info.foodPreference.trim() === '') {
        setPopupMessage('Please select a meal plan for daycare.');
        setShowPopup(true);
        return;
      }
    }

    // ----- 2. Hotel -----
    if (type === 'store/services/hotel') {
      if (!Array.isArray(info.dateRange) || info.dateRange.length !== 2) {
        setPopupMessage('Please select check-in and check-out dates.');
        setShowPopup(true);
        return;
      }
      if (!info.selectedPets || info.selectedPets.length === 0) {
        setPopupMessage('Please select a pet for hotel booking.');
        setShowPopup(true);
        return;
      }
      if (!info.foodPreference || info.foodPreference.trim() === '') {
        setPopupMessage('Please choose a meal plan for your pet’s hotel stay.');
        setShowPopup(true);
        return;
      }
    }

    // ----- 3. Grooming -----
    if (type === 'store/services/grooming') {
      if (!info.selectedPets || info.selectedPets.length === 0) {
        setPopupMessage('Please select a pet for grooming.');
        setShowPopup(true);
        return;
      }
      if (!info.dateRange || !info.dateRange[0]) {
        setPopupMessage('Please select a grooming date.');
        setShowPopup(true);
        return;
      }
      if (!info.selectedTimeSlot) {
        setPopupMessage('Please choose a grooming time slot.');
        setShowPopup(true);
        return;
      }
    }

    // ----- 4. Training -----
    if (type === 'store/services/training') {
      if (!info.selectedPets || info.selectedPets.length === 0) {
        setPopupMessage('Please select a pet for training.');
        setShowPopup(true);
        return;
      }
      if (!info.dateRange || !info.dateRange[0]) {
        setPopupMessage('Please select a training date.');
        setShowPopup(true);
        return;
      }
      if (!info.selectedTimeSlot) {
        setPopupMessage('Please choose a training time slot.');
        setShowPopup(true);
        return;
      }
    }

    // ----- 5. Shuttle -----
    if (type === 'store/services/shuttle') {
      if (!info.selectedPets || info.selectedPets.length === 0) {
        setPopupMessage('Please select a pet for shuttle service.');
        setShowPopup(true);
        return;
      }
      if (!info.dateRange || !info.dateRange[0]) {
        setPopupMessage('Please select a shuttle date.');
        setShowPopup(true);
        return;
      }
      if (!info.selectedTimeSlot) {
        setPopupMessage('Please choose a shuttle time.');
        setShowPopup(true);
        return;
      }
      if (!info.pickUpLocation || !info.dropOffLocation) {
        setPopupMessage('Please enter pickup and drop-off locations.');
        setShowPopup(true);
        return;
      }
    }

    // ----- 6. Adventure -----
    if (type === 'store/services/adventure') {
      if (!info.selectedPets || info.selectedPets.length === 0) {
        setPopupMessage('Please select a pet for adventure day.');
        setShowPopup(true);
        return;
      }
      if (!info.dateRange || !info.dateRange[0]) {
        setPopupMessage('Please select an adventure day.');
        setShowPopup(true);
        return;
      }
      if (!info.selectedTimeSlot) {
        setPopupMessage('Please choose a time.');
        setShowPopup(true);
        return;
      }
    }

    const { originalPrice, discountedPrice } = calculatePrice();
    var bookingTypeCapitalized =
      bookingData.bookingType.split('/').pop().charAt(0).toUpperCase() +
      bookingData.bookingType.split('/').pop().slice(1);

    if (bookingData.bookingType === 'store/membership') {
      if (
        bookingData.bookingInfo &&
        bookingData.bookingInfo.selectedPackage &&
        bookingData.bookingInfo.selectedPackage !== ''
      ) {
        bookingTypeCapitalized =
          'Membership - ' + bookingData.bookingInfo.selectedPackage;
      }
    }

    if (bookingData.bookingType === 'store/bundle') {
      if (
        bookingData.bookingInfo &&
        bookingData.bookingInfo.selectedPackage &&
        bookingData.bookingInfo.selectedPackage !== ''
      ) {
        bookingTypeCapitalized =
          'Daycare Bundle';
      }
    }

    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    var normalizedDateRange =
      bookingData.bookingInfo && bookingData.bookingInfo.dateRange;

    if (Array.isArray(normalizedDateRange) && normalizedDateRange.length === 2) {
      var s = moment.tz(normalizedDateRange[0], tz).startOf('day').toDate().toISOString();
      var e = moment.tz(normalizedDateRange[1], tz).endOf('day').toDate().toISOString();
      normalizedDateRange = [s, e];
    }

    const bookingDetails = {
      userId: user.uid,
      serviceId: serviceId,
      quantity: bookingData.quantity || 1,
      originalPrice,
      discountedPrice,
      bookingType: bookingTypeCapitalized,
      bookingInfo: {
        ...(bookingData.bookingInfo || {}),
        timezone: tz,
        dateRange: normalizedDateRange,
      },
    };

    try {
      const addedItem = await addCartItem(bookingDetails);
      setCart(currentCart => [...currentCart, addedItem]);
      refreshUserData(user.uid);
      navigate('/profile/cart');
      window.scrollTo(0, 0);
    } catch (error) {
      console.error('Error adding to cart:', error);
    }
  };

  // --- SERVICE ID FINDER ---
  function buildServiceIndex(serviceDetails) {
    var index = {};
    var categories = Object.keys(serviceDetails || {});
    for (var i = 0; i < categories.length; i++) {
      var list = serviceDetails[categories[i]];
      if (!list || !list.length) continue;
      for (var j = 0; j < list.length; j++) {
        var s = list[j];
        if (s && s.serviceName)
          index[s.serviceName.toLowerCase()] = s.id;
        if (s && s.title)
          index[s.title.toLowerCase()] = s.id;
      }
    }
    return index;
  }

  function findServiceId(serviceDetails, bookingType, bookingData) {
    if (!serviceDetails || !bookingType) return null;
    var idx = buildServiceIndex(serviceDetails);
    var parts = bookingType.split('/');
    var leaf = parts[parts.length - 1].toLowerCase();

    // Membership
    if (bookingType.indexOf('membership') !== -1) {
      var pkg = bookingData && bookingData.bookingInfo && bookingData.bookingInfo.selectedPackage;
      if (pkg) {
        var normalized = pkg.toLowerCase();
        var key = normalized === 'club'
          ? 'pawsible club'
          : 'pawsible ' + normalized + ' club';
        if (idx[key]) return idx[key];
      }
    }

    // Bundle
    if (bookingType.indexOf('bundle') !== -1) {
      var pack = bookingData && bookingData.bookingInfo && bookingData.bookingInfo.selectedPackage;
      if (pack) {
        var bundleKey = ('daycare ' + pack).toLowerCase(); // e.g. "daycare 10 pack"
        if (idx[bundleKey]) return idx[bundleKey];
        if (idx['daycare bundle']) return idx['daycare bundle'];
      }
    }

    // Gift Card
    if (bookingType.indexOf('gift-card') !== -1) {
      if (idx['gift card - custom']) return idx['gift card - custom'];
      if (idx['gift card']) return idx['gift card'];
      return null;
    }

    // Shuttle
    if (leaf === 'shuttle') {
      var shuttleName =
        bookingData &&
        bookingData.serviceDetails &&
        bookingData.serviceDetails.serviceName
          ? bookingData.serviceDetails.serviceName.toLowerCase()
          : '';
      if (shuttleName && idx[shuttleName]) return idx[shuttleName];
    }

    if (idx[leaf]) return idx[leaf];
    return null;
  }

  const handleCartClick = () => {
    navigate('/profile/cart');
    window.scrollTo(0, 0);
  };

  var dateRangeText =
    bookingData.bookingType !== 'store/services/hotel'
      ? formatDateRange(
          bookingData.bookingInfo && bookingData.bookingInfo.dateRange
            ? bookingData.bookingInfo.dateRange
            : new Date()
        )
      : formatDateRange(
          bookingData.bookingInfo && bookingData.bookingInfo.dateRange
            ? bookingData.bookingInfo.dateRange
            : [new Date(), new Date()]
        );

  var nightsText = '';
  if (bookingData.bookingType === 'store/services/hotel') {
    var nightsCount = getHotelNights(
      bookingData.bookingInfo && bookingData.bookingInfo.dateRange
    );
    if (nightsCount > 0)
      nightsText = ` (${nightsCount} night${nightsCount > 1 ? 's' : ''})`;
  }

  var daysText = '';
  if (bookingData.bookingType === 'store/services/daycare') {
    var daysCount = getDaycareDays(
      bookingData.bookingInfo && bookingData.bookingInfo.dateRange
    );
    if (daysCount > 0)
      daysText = ` (${daysCount} day${daysCount > 1 ? 's' : ''})`;
  }

  return (
    <div>
      <Popup isOpen={showPopup} handleClose={() => setShowPopup(false)}>
        <div className="booking-dashboard-popup-message-container">
          <p className="booking-dashboard-popup-message">{popupMessage}</p>
        </div>
        <div className="booking-dashboard-popup-buttons-container">
          <button
            className="booking-dashboard-popup-ok-button"
            onClick={() => setShowPopup(false)}
          >
            OK
          </button>
        </div>
      </Popup>

      <footer className="booking-dashboard">
        <Container className="booking-dashboard-container">
          <div className="booking-payment-info">
            <div className="booking-dashboard-prices">
              {originalPrice === discountedPrice ? null : (
                <p className="booking-dashboard-total">${originalPrice.toFixed(0)}</p>
              )}
              <p className="booking-dashboard-discount">
                ${discountedPrice.toFixed(0)} total
              </p>
            </div>
            <p className="booking-dashboard-date">
              {dateRangeText} {nightsText} {daysText}
            </p>
          </div>
          <button onClick={handleAddToCart}>Add</button>
        </Container>
      </footer>

      <div className="booking-dashboard-cart-icon-container">
        <img
          className="booking-dashboard-cart-icon"
          src={cartIcon}
          onClick={handleCartClick}
        />
        {cartItemCount > 0 && <span className="cart-item-count">{cartItemCount}</span>}
      </div>
    </div>
  );
};

export default BookingDashboard;
